import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Image,
  Pressable,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { DataTable } from "react-native-paper";
import { useTheme } from "@/src/styles/theme-provider";
import ScPagination from "../../ui-kit/pagination";
import { UserRemove, UserTick } from "iconsax-react-nativejs";
import { PagyMeta } from "@/src/screens/groups/group-members";
import StateDisplay from "../../ui-kit/state-display";
import BlocksAvatar from "../../ui-kit/blocks-avatar";
import { GroupMember } from "@/src/models/user.model";
import { formatDisplayDate } from "@/src/utils/date.utils";
import MemberRowSkeleton from "./member-row-skeleton";

type PendingMembersTableProps = Readonly<{
  pendingMembers: GroupMember[];
  pendingPagy?: PagyMeta;
  onPendingPageChange: (page: number) => void;
  addMembership: (membershipId: string) => void;
  handleRemoveMembership: (membershipId: string, toastKey: string) => void;
  isActionPending: boolean;
  isLoading?: boolean;
}>;

const COL = {
  name: { flex: 2 },
  email: { flex: 3 },
  date: { flex: 1.5 },
  action: { flex: 1 },
};

const PendingMembers = ({
  pendingMembers,
  pendingPagy,
  onPendingPageChange,
  addMembership,
  handleRemoveMembership,
  isActionPending,
  isLoading = false,
}: PendingMembersTableProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  return (
    <View className="flex-1">
      {isMobile ? (
        /* ── Mobile: card list ── */
        <View className="gap-3 pt-1">
          {isLoading ? (
            <View className="py-10 items-center">
              <ActivityIndicator
                color={themeVars["--color-primary-default"] as string}
              />
            </View>
          ) : pendingMembers.length === 0 ? (
            <StateDisplay
              variant="error"
              descriptionLabel="LABEL_EMPTY_GROUP_PENDING_REQUEST"
              imgSource={require("@/src/assets/images/empty.png")}
            />
          ) : (
            pendingMembers.map((member) => (
              <View
                key={member.itemId}
                className="bg-chalk rounded-xl p-3 gap-3"
              >
                <View className="flex-row items-center gap-3">
                  <BlocksAvatar
                    userId={member.userId}
                    fallbackImageUrl={member.profileImageUrl}
                    size={40}
                  />
                  <View className="flex-1">
                    <Text
                      className="text-sm font-semibold text-onyx"
                      numberOfLines={1}
                    >
                      {member.firstName} {member.lastName}
                    </Text>
                    <Text className="text-xs text-slate" numberOfLines={1}>
                      {member.email}
                    </Text>
                  </View>
                </View>

                <View className="border-t border-ash-3" />

                <View className="flex-row items-center justify-between gap-2">
                  <View className="gap-0.5">
                    <Text className="text-xs text-slate">
                      {t("LABEL_REQUEST_OR_INVITE_DATE")}
                    </Text>
                    <Text className="text-xs font-medium text-onyx">
                      {formatDisplayDate(member.joinedAt)}
                    </Text>
                  </View>

                  {isActionPending ? (
                    <ActivityIndicator size="small" />
                  ) : (
                    <View className="flex-row gap-4 items-center">
                      <Pressable
                        onPress={() => addMembership(member.itemId)}
                        hitSlop={8}
                        className="active:opacity-70 p-1.5 hover:bg-ash-3 hover:rounded-full"
                      >
                        <UserTick
                          color={themeVars["--color-jade-default"] as string}
                        />
                      </Pressable>
                      <Pressable
                        onPress={() =>
                          handleRemoveMembership(
                            member.itemId,
                            "LABEL_REQUEST_REJECTED",
                          )
                        }
                        hitSlop={8}
                        className="active:opacity-70 p-1.5 hover:bg-ash-3 hover:rounded-full"
                      >
                        <UserRemove
                          color={themeVars["--color-crimson-default"] as string}
                        />
                      </Pressable>
                    </View>
                  )}
                </View>
              </View>
            ))
          )}
        </View>
      ) : (
        /* ── Desktop: full table ── */
        <DataTable>
          <DataTable.Header
            style={{
              borderBottomColor: themeVars["--color-ash-3"] as string,
              borderBottomWidth: 1,
            }}
          >
            <DataTable.Title style={COL.name}>
              {t("LABEL_NAME")}
            </DataTable.Title>
            <DataTable.Title style={COL.email}>
              {t("LABEL_EMAIL")}
            </DataTable.Title>
            <DataTable.Title style={COL.date}>
              {t("LABEL_REQUEST_OR_INVITE_DATE")}
            </DataTable.Title>
            <DataTable.Title style={COL.action}>
              {t("LABEL_ACTION")}
            </DataTable.Title>
          </DataTable.Header>

          {isLoading ? (
            <MemberRowSkeleton count={5} />
          ) : pendingMembers.length === 0 ? (
            <DataTable.Row>
              <DataTable.Cell style={{ flex: 1 }}>
                <StateDisplay
                  variant="error"
                  descriptionLabel="LABEL_EMPTY_GROUP_PENDING_REQUEST"
                  imgSource={require("@/src/assets/images/empty.png")}
                />
              </DataTable.Cell>
            </DataTable.Row>
          ) : (
            <>
              {pendingMembers.map((member) => (
                <DataTable.Row
                  style={{
                    borderBottomColor: themeVars["--color-ash-3"] as string,
                    borderBottomWidth: 1,
                  }}
                  key={member.itemId}
                >
                  <DataTable.Cell style={COL.name}>
                    <View className="flex-row items-center gap-2 flex-1">
                      <BlocksAvatar
                        className="hidden sm:flex"
                        userId={member.userId}
                        fallbackImageUrl={member.profileImageUrl}
                        size={32}
                      />
                      <Text className="flex-1 overflow-ellipsis text-onyx">
                        {member.firstName} {member.lastName}
                      </Text>
                    </View>
                  </DataTable.Cell>
                  <DataTable.Cell style={COL.email}>
                    {member.email}
                  </DataTable.Cell>
                  <DataTable.Cell style={COL.date}>
                    {formatDisplayDate(member.joinedAt)}
                  </DataTable.Cell>
                  <DataTable.Cell style={COL.action}>
                    {isActionPending ? (
                      <ActivityIndicator size="small" />
                    ) : (
                      <View className="flex-row gap-3 items-center">
                        <Pressable
                          onPress={() => addMembership(member.itemId)}
                          className="active:opacity-70 p-1.5 hover:bg-ash-3 hover:rounded-full"
                        >
                          <UserTick color={themeVars["--color-jade-default"] as string} />
                        </Pressable>
                        <Pressable
                          onPress={() =>
                            handleRemoveMembership(
                              member.itemId,
                              "LABEL_REQUEST_REJECTED",
                            )
                          }
                          className="active:opacity-70 p-1.5 hover:bg-ash-3 hover:rounded-full"
                        >
                          <UserRemove color={themeVars["--color-crimson-default"] as string} />
                        </Pressable>
                      </View>
                    )}
                  </DataTable.Cell>
                </DataTable.Row>
              ))}
            </>
          )}
        </DataTable>
      )}

      {pendingPagy && (
        <ScPagination
          currentPage={pendingPagy.current_page}
          totalPages={pendingPagy.total_pages}
          totalItems={pendingPagy.total_count}
          from={pendingPagy.from}
          to={pendingPagy.to}
          onPageChange={onPendingPageChange}
        />
      )}
    </View>
  );
};

export default PendingMembers;
